import {
  SignOutDocument,
  SignOutMutation,
  SignOutMutationVariables
} from "@pathwright/graphql-client/src/generated"
import { getClient, PW_AUTH_TOKEN_KEY } from "../../api/client"

// This an expensive import if all we want to do is remove the key.
// import { discardAuthEmailFormStateStorage } from "../../views/auth/utils/authEmailFormStateStorage"

export type TokenEventDetail = {
  token: string | null
  source: "auth"
}

export type AuthenticationTokenData = {
  aud: "auth"
  iss: `school:${number}`
  sub: `user:${number}`
  exp: number
  iat: number
  jti: string
  proxier: number
}

// Extending the CustomEventMap with our own `PW_AUTH_TOKEN_KEY` event key.
interface CustomEventMap {
  [PW_AUTH_TOKEN_KEY]: CustomEvent<TokenEventDetail>
}

const handleAuthTokenEvents = () => {
  const dispatchAuthTokenEvent = (token: string | null) => {
    window.dispatchEvent(
      new CustomEvent<TokenEventDetail>(PW_AUTH_TOKEN_KEY, {
        detail: {
          token,
          source: "auth"
        }
      })
    )
  }

  // Let's handle any `PW_AUTH_TOKEN_KEY` event not triggered by our "auth" token handlers.
  // This allows for syncing auth tokens issued by outside sources, like our bookmarklet
  // authentication shortcut (https://www.notion.so/pathwright/Sign-in-as-anyone-with-a-SuperUser-override-5b0ada88ef4e47e79c419f3baae1941a#2455d3b96ada42309574635fa82f3e4d).
  window.addEventListener(PW_AUTH_TOKEN_KEY, async (e) => {
    if (e.detail.source !== "auth") {
      if (e.detail.token) {
        // If an auth token is currently stored and a user is authenticating,
        // then, to ensure the previous user's session has ended, we first sign them out.
        if (localStorage.getItem(PW_AUTH_TOKEN_KEY)) {
          const client = getClient()
          await client.mutate<SignOutMutation, SignOutMutationVariables>({
            mutation: SignOutDocument
          })
        }
        handleAuthToken(e.detail.token)
      } else {
        clearAuthenticationToken()
      }
    }
  })

  return dispatchAuthTokenEvent
}

const dispatchAuthTokenEvent = handleAuthTokenEvents()

export const getAuthenticationToken = () => {
  return localStorage.getItem(PW_AUTH_TOKEN_KEY)
}

export const hasAuthenticationToken = () => {
  return getAuthenticationToken() !== null
}

let pendingReset: { token: string | null; promise: Promise<void> } | null = null

// Clears the Apollo cache and refetches the active queries. An auth change can
// request this from several places at once (the token handlers below and host
// auth sync), so calls for the same token share the in-flight reset.
export const resetClientStore = () => {
  const token = getAuthenticationToken()
  if (pendingReset?.token === token) return pendingReset.promise

  const promise = getClient()
    .resetStore()
    .then(
      () => undefined,
      (error) => {
        console.log("error", error)
      }
    )
    .finally(() => {
      if (pendingReset?.promise === promise) pendingReset = null
    })
  pendingReset = { token, promise }
  return promise
}

// NOTE: to avoid importing all of form state storage as a dep,
// we're just hard coding the form state storage key.

export const clearAuthenticationToken = async () => {
  localStorage.removeItem(PW_AUTH_TOKEN_KEY)
  dispatchAuthTokenEvent(null)
  await resetClientStore()
  // discardAuthEmailFormStateStorage()
  sessionStorage.removeItem("formState:auth-email")
  return null
}

export const handleAuthToken = async (token: string) => {
  localStorage.setItem(PW_AUTH_TOKEN_KEY, token)
  dispatchAuthTokenEvent(token)
  await resetClientStore()
  // discardAuthEmailFormStateStorage()
  sessionStorage.removeItem("formState:auth-email")
  return null
}
